import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import useNotifications from "./notificationStore";
import { Get } from "api";

interface Invoice {
  partnerId: number;
  amount: number;
  processStatus: string;
  paymentStatus: string;
  rangeFrom: string;
  rangeUntil: string;
  dueDate: string;
  status?: string;
}

interface InvoiceStore {
  overdueInvoices: Invoice[];
  unpaidInvoices: Invoice[];
  invoices?: Invoice[];
  fetchInvoices: () => Promise<void>;
  loading: boolean;
  resetInvoices: () => void;
}

export const useInvoicesStore = create<InvoiceStore>()(
  subscribeWithSelector((set) => ({
    overdueInvoices: [],
    unpaidInvoices: [],
    loading: true,
    resetInvoices: () => set({ overdueInvoices: [], unpaidInvoices: [] }),
    fetchInvoices: async () => {
      set({ loading: true });
      try {
        const response = await Get("/dashboard/invoice/unpaid");
        const { overdueInvoices, unpaidInvoices } = response;

        const overdueWithStatus = overdueInvoices.map((invoice: Invoice) => ({
          ...invoice,
          status: "OVERDUE",
        }));

        const unpaidWithStatus = unpaidInvoices
          .map((invoice: Invoice) => ({
            ...invoice,
            status: "UNPAID",
          }))
          .filter(
            (unpaidInvoice: Invoice) =>
              !overdueInvoices.some(
                (overdueInvoice: Invoice) =>
                  overdueInvoice.rangeUntil === unpaidInvoice.rangeUntil &&
                  overdueInvoice.rangeFrom === unpaidInvoice.rangeFrom &&
                  overdueInvoice.amount === unpaidInvoice.amount
              )
          );

        const invoices = [...overdueWithStatus, ...unpaidWithStatus];

        set({
          overdueInvoices: overdueWithStatus,
          unpaidInvoices: unpaidWithStatus,
          invoices,
        });
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        set({ loading: false });
      }
    },
  }))
);

const notificationStore = useNotifications.getState();

useInvoicesStore.subscribe(
  (state) => state.overdueInvoices,
  (overdueInvoices) => {
    if (overdueInvoices.length > 0) {
      notificationStore.addNotificationMessage(
        "You have unpaid invoices that are past due.",
        "To avoid service disruption, please settle your outstanding balance immediately. Failure to do so may result in temporary account suspension. For more details or assistance, please contact your Account Manager.",
        "error",
        "overdueInvoice"
      );
    }
  }
);
